import {observer} from 'mobx-react-lite';
import {State} from '../../../entity/model/state';
import {County} from '../../../entity/model/county';
import {LineListRequest} from '../../../entity/model/line-list-request';
import {LineListTemplate} from '../../../entity/model/line-list-template';
import {VestingDeed} from '../../../entity/resource/vesting-deed';
import {Estate} from '../../../entity/resource/estate';
import {TaxYear} from '../../../entity/resource/tax-year';
import {MobileHome, MobileHomeTaxYear} from '../../../entity/resource/mobile-home';
import {RestrictionAgreement} from '../../../entity/resource/restriction-agreement';
import {EasementRightOfWay} from '../../../entity/resource/easement-right-of-way';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {useService} from '../../../infrastructure/service/service';
import {useState, useEffect} from 'react';
import {SearchType} from '../../../entity/enums/search-type';
import {DropdownOption} from '../../../entity/core/dropdown-option';
import {EstateStatus} from '../../../entity/enums/estate-status';
import {LogInfoStatus} from '../../../entity/enums/log-info-status';
import {ManageMode} from '../../../entity/enums/manage-mode';
import moment from 'moment/moment';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import LoadingComponent from '../components/widget/LoadingComponent';
import LineListTemplateDialog from '../components/report/LineListTemplateDialog';
import { toast } from 'react-toastify';

import '../../../assets/css/style.css';
import '../../../assets/css/pages/line-list-report.css';

const LineListReport = (): JSX.Element => {
  const {lineListRequestService, stateService, countyService, documentService} = useService();

  const [searchTypeOptions, setSearchTypeOptions] = useState<DropdownOption[]>([]);
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState<boolean>(false);
  const [dialogMode, setDialogMode] = useState<ManageMode>(ManageMode.EXPORT);
  const [currentFormValues, setCurrentFormValues] = useState<LineListRequest>(lineListRequestService.getLineListRequestDto());

  useEffect(() => {
    const init = async (): Promise<void> => {
      lineListRequestService.loadSharedData();

      const searchTypes = Object.keys(SearchType).map((key: string) => ({
        key: key,
        text: SearchType[key as keyof typeof SearchType],
        value: key
      }));
      setSearchTypeOptions(searchTypes);

      const statesData = await stateService.getStates();
      setStates(statesData);
      setStateOptions(statesData.map((state: State) => ({key: state.id, text: state.shortName, value: state.id})));

      const countiesData = await countyService.getCounties();
      setCounties(countiesData);

      const currentStateId = lineListRequestService.getCurrentStateId();
      if (currentStateId) {
        const stateCounties = countiesData.filter((county: County) => county.stateID === currentStateId).sort((a: County, b: County) => a.name.localeCompare(b.name));
        setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));
      } else
        setCountyOptions(countiesData.filter((county: County) => county.stateID === statesData[0].id).sort((a: County, b: County) => a.name.localeCompare(b.name)).map((county: County) => ({key: county.id, text: county.name, value: county.id})));

      setIsDataLoaded(true);
    }

    init();
  }, []);

  const handleExportTemplate = (currentValues: LineListRequest): void => {
    setCurrentFormValues(currentValues);
    setDialogMode(ManageMode.EXPORT);
    setOpenTemplateDialog(true);
  }

  const handleImportTemplate = (): void => {
    setDialogMode(ManageMode.IMPORT);
    setOpenTemplateDialog(true);
  }

  const handleTemplateSelect = (template: LineListTemplate): void => {
    const initialValues = lineListRequestService.getLineListRequestDto();
    const updatedValues = { ...currentFormValues };

    if (template.orderInformation) {
      updatedValues.orderInformation = template.orderInformation;
    } else {
      updatedValues.orderInformation = initialValues.orderInformation;
    }

    if (template.propertyInformation) {
      updatedValues.propertyInformation = template.propertyInformation;
    } else {
      updatedValues.propertyInformation = initialValues.propertyInformation;
    }

    if (template.ownerInformation)
      updatedValues.ownerInformation = template.ownerInformation;

    if (template.occupantInformation)
      updatedValues.occupantInformation = template.occupantInformation;

    if (template.acquisitionDetails)
      updatedValues.acquisitionDetails = template.acquisitionDetails;

    if (template.vestingDeed)
      updatedValues.vestingDeed = template.vestingDeed;

    if (template.estates)
      updatedValues.estates = template.estates;

    if (template.taxYears)
      updatedValues.taxYears = template.taxYears;

    if (template.taxFlags)
      updatedValues.taxFlags = template.taxFlags;

    if (template.mobileHome)
      updatedValues.mobileHome = template.mobileHome;

    if (template.restrictionsAgreements)
      updatedValues.restrictionsAgreements = template.restrictionsAgreements;

    if (template.easementsRightOfWays)
      updatedValues.easementsRightOfWays = template.easementsRightOfWays;

    if (template.comment)
      updatedValues.comment = template.comment;

    setCurrentFormValues(updatedValues);

    const currentProps = lineListRequestService.getCurrentProps();
    if (currentProps) {
      const reportDataWithPreservedIds = {
        ...updatedValues,
        _id: currentProps.reportData?._id ?? updatedValues._id,
        documentID: currentProps.reportData?.documentID ?? updatedValues.documentID,
        projectTitleID: currentProps.reportData?.projectTitleID ?? updatedValues.projectTitleID,
        createdByUserID: currentProps.reportData?.createdByUserID ?? updatedValues.createdByUserID,
        createdAt: currentProps.reportData?.createdAt ?? updatedValues.createdAt,
        updatedAt: currentProps.reportData?.updatedAt ?? updatedValues.updatedAt
      };
      
      lineListRequestService.setCurrentProps({
        ...currentProps,
        reportData: reportDataWithPreservedIds
      });
    }

    toast.success('Template imported successfully!');
  }

  const handleSubmit = async (values: LineListRequest, {setSubmitting}: FormikHelpers<LineListRequest>): Promise<void> => {
    setSubmitting(true);

    const props = lineListRequestService.getCurrentProps();
    const sanitizedData = lineListRequestService.getSanitizeLineListRequest(values, props?.title?.id);
    const state = states.find(state => state.id === values.propertyInformation?.stateID);
    const county = counties.find(county => county.id === values.propertyInformation?.countyID);

    const pdfBlob = await lineListRequestService.generateLineListReportPdf(sanitizedData, state, county);

    if (!props?.title || !props?.project) {
      toast.error('Title or project information not available');
      setSubmitting(false);
      return;
    }

    const fileName = `Line_List_Report_${sanitizedData.orderInformation?.orderNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
    const pdfFile = new File([pdfBlob], fileName, {
      type: 'application/pdf',
      lastModified: Date.now()
    });

    let targetSection: any | undefined;
    
    if (props.sectionId) 
      targetSection = props.title.sections?.find((section: any) => section.id === props.sectionId);
    
    if (!targetSection) 
      targetSection = props.title.sections?.find((section: any) => section.name === 'Unclassified');
  
    if (!targetSection) {
      toast.error('Section not found. Please create an "Unclassified" section first.');
      setSubmitting(false);
      return;
    }

    if (props.mode === ManageMode.UPDATE && props.reportData?.documentID) {
      const existingDocumentSection = props.title.sections?.find((section: any) => section.documents?.some((doc: any) => doc.id === props.reportData?.documentID));

      if (existingDocumentSection && props.reportData?.documentID)
        await documentService.deleteDocument(props.title.createdByOrganizationID, existingDocumentSection.id, props.reportData.documentID, false, props.title.id);
    }

    const document = await documentService.uploadFile(props.project.createdByOrganizationID, pdfFile, props.title.id, targetSection.id);
    if (!document) {
      toast.error('Failed to upload document');
      setSubmitting(false);
      return;
    }

    sanitizedData.documentID = document.id;

    if (props.mode === ManageMode.UPDATE && props.reportData)
      await lineListRequestService.updateLineListRequest(props.project.createdByOrganizationID, props.reportData._id ?? '', sanitizedData);
    else
      await lineListRequestService.createLineListRequest(props.project.createdByOrganizationID, sanitizedData);

    const broadcastChannel = new BroadcastChannel('title-updates');
    broadcastChannel.postMessage({
      type: 'title-document-updated',
      titleId: props.title.id
    });
    broadcastChannel.close();

    setSubmitting(false);
  }

  const handleDeleteVestingDeed = (values: LineListRequest, index: number, handleChange: any): void => {
    const newDeeds = values.vestingDeed?.filter((_: VestingDeed, i: number) => i !== index) ?? [];
    handleChange({
      target: {
        name: 'vestingDeed',
        value: newDeeds
      }
    });
  }

  const handleAddVestingDeed = (values: LineListRequest, handleChange: any): void => {
    const newDeed: VestingDeed = {
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      platReference: undefined,
      legalDescription: undefined
    };
    const newDeeds = [...(values.vestingDeed ?? []), newDeed];
    handleChange({
      target: {
        name: 'vestingDeed',
        value: newDeeds
      }
    });
  }

  const handleStateChange = (handleChange: any) => (event: any): void => {
    const stateCounties = counties.filter((county: County) => county.stateID === event.target.value).sort((a: County, b: County) => a.name.localeCompare(b.name));
    setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

    handleChange({target: {name: event.target.name, value: event.target.value}});
  }

  const handleCountyChange = (handleChange: any) => (event: any): void => {
    handleChange({target: {name: event.target.name, value: event.target.value}});
  }

  const handleAddEstate = (values: LineListRequest, handleChange: any): void => {
    const newEstate: Estate = {
      estateStatus: EstateStatus.OPEN,
      estateNumber: undefined,
      decedentName: undefined,
      heirsListed: false,
      heirsSearched: false
    };

    const newEstates = [...(values.estates ?? []), newEstate];
    handleChange({
      target: {
        name: 'estates',
        value: newEstates
      }
    });
  }

  const handleDeleteEstate = (values: LineListRequest, index: number, handleChange: any): void => {
    const newEstates = values.estates?.filter((_: Estate, i: number) => i !== index) ?? [];
    handleChange({
      target: {
        name: 'estates',
        value: newEstates
      }
    });
  }

  const handleAddTaxYear = (values: LineListRequest, handleChange: any): void => {
    const newTaxYear: TaxYear = {
      year: '',
      amountOwed: '',
      amountPaid: ''
    };

    const newTaxYears = [...(values.taxYears ?? []), newTaxYear];
    handleChange({
      target: {
        name: 'taxYears',
        value: newTaxYears
      }
    });
  }

  const handleDeleteTaxYear = (values: LineListRequest, index: number, handleChange: any): void => {
    const newTaxYears = values.taxYears?.filter((_: TaxYear, i: number) => i !== index) ?? [];
    handleChange({
      target: {
        name: 'taxYears',
        value: newTaxYears
      }
    });
  }

  const handleAddMobileHome = (values: LineListRequest, handleChange: any): void => {
    const newMobileHome: MobileHome = {
      year: undefined,
      make: undefined,
      model: undefined,
      serialNumber: undefined,
      parcelNumber: undefined,
      decal: undefined
    };

    const newMobileHomes = [...(values.mobileHome ?? []), newMobileHome];
    handleChange({
      target: {
        name: 'mobileHome',
        value: newMobileHomes
      }
    });
  }

  const handleDeleteMobileHome = (values: LineListRequest, index: number, handleChange: any): void => {
    const newMobileHomes = values.mobileHome?.filter((_: MobileHome, i: number) => i !== index) ?? [];
    handleChange({
      target: {
        name: 'mobileHome',
        value: newMobileHomes
      }
    });
  }

  const handleAddMobileHomeTaxYear = (values: LineListRequest, mobileHomeIndex: number, handleChange: any): void => {
    const newTaxYear: MobileHomeTaxYear = {
      year: '',
      amountOwed: '',
      amountPaid: ''
    };

    const currentMobileHome = values.mobileHome?.[mobileHomeIndex];
    if (!currentMobileHome) return;

    const newTaxYears = [...(currentMobileHome.taxYears ?? []), newTaxYear];
    const updatedMobileHomes = [...(values.mobileHome ?? [])];
    updatedMobileHomes[mobileHomeIndex] = {
      ...currentMobileHome,
      taxYears: newTaxYears
    };

    handleChange({
      target: {
        name: 'mobileHome',
        value: updatedMobileHomes
      }
    });
  }

  const handleDeleteMobileHomeTaxYear = (values: LineListRequest, mobileHomeIndex: number, taxIndex: number, handleChange: any): void => {
    const currentMobileHome = values.mobileHome?.[mobileHomeIndex];
    if (!currentMobileHome) return;

    const newTaxYears = currentMobileHome.taxYears?.filter((_: MobileHomeTaxYear, i: number) => i !== taxIndex) ?? [];
    const updatedMobileHomes = [...(values.mobileHome ?? [])];
    updatedMobileHomes[mobileHomeIndex] = {
      ...currentMobileHome,
      taxYears: newTaxYears
    };

    handleChange({
      target: {
        name: 'mobileHome',
        value: updatedMobileHomes
      }
    });
  }

  const handleMobileHomeLienRetirementChange = (values: LineListRequest, mobileHomeIndex: number, handleChange: any) => (e: any): void => {
    const currentMobileHome = values.mobileHome?.[mobileHomeIndex];
    if (!currentMobileHome) return;

    if (e.target.checked && !currentMobileHome.lienRetirement) {
      const updatedMobileHomes = [...(values.mobileHome ?? [])];
      updatedMobileHomes[mobileHomeIndex] = {
        ...currentMobileHome,
        lienRetirement: {
          recordedDate: undefined,
          book: undefined,
          page: undefined
        }
      };
      handleChange({
        target: {
          name: 'mobileHome',
          value: updatedMobileHomes
        }
      });
    } else if (!e.target.checked) {
      const updatedMobileHomes = [...(values.mobileHome ?? [])];
      updatedMobileHomes[mobileHomeIndex] = {
        ...currentMobileHome,
        lienRetirement: undefined
      };
      handleChange({
        target: {
          name: 'mobileHome',
          value: updatedMobileHomes
        }
      });
    }
  }

  const handleAddRestriction = (values: LineListRequest, handleChange: any): void => {
    const newRestriction: RestrictionAgreement = {
      title: undefined,
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      description: undefined
    };

    const newRestrictions = [...(values.restrictionsAgreements ?? []), newRestriction];
    handleChange({
      target: {
        name: 'restrictionsAgreements',
        value: newRestrictions
      }
    });
  }

  const handleDeleteRestriction = (values: LineListRequest, index: number, handleChange: any): void => {
    const newRestrictions = values.restrictionsAgreements?.filter((_: RestrictionAgreement, i: number) => i !== index) ?? [];
    handleChange({
      target: {
        name: 'restrictionsAgreements',
        value: newRestrictions
      }
    });
  }

  const handleAddEasement = (values: LineListRequest, handleChange: any): void => {
    const newEasement: EasementRightOfWay = {
      title: undefined,
      type: undefined,
      recordedDate: undefined,
      book: undefined,
      page: undefined,
      description: undefined
    };

    const newEasements = [...(values.easementsRightOfWays ?? []), newEasement];
    handleChange({
      target: {
        name: 'easementsRightOfWays',
        value: newEasements
      }
    });
  }

  const handleDeleteEasement = (values: LineListRequest, index: number, handleChange: any): void => {
    const newEasements = values.easementsRightOfWays?.filter((_: EasementRightOfWay, i: number) => i !== index) ?? [];
    handleChange({
      target: {
        name: 'easementsRightOfWays',
        value: newEasements
      }
    });
  }

  const handleEstatesYesChange = (values: any, handleChange: any) => (e: any): void => {
    if (e.target.checked && values.estates.length === 0)
      handleAddEstate(values, handleChange);
    else if (!e.target.checked)
      handleChange({
        target: {
          name: 'estates',
          value: []
        }
      });
  }

  const handleEstatesNoChange = (values: any, handleChange: any) => (e: any): void => {
    if (!e.target.checked && values.estates.length === 0)
      handleAddEstate(values, handleChange);
    else if (e.target.checked)
      handleChange({
        target: {
          name: 'estates',
          value: []
        }
      });
  }

  const handleMobileHomeYesChange = (values: LineListRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.mobileHome?.length ?? 0) === 0)
      handleAddMobileHome(values, handleChange);
    else if (!e.target.checked)
      handleChange({
        target: {
          name: 'mobileHome',
          value: []
        }
      });
  }

  const handleMobileHomeNoChange = (values: LineListRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.mobileHome?.length ?? 0) === 0)
      handleAddMobileHome(values, handleChange);
    else if (e.target.checked)
      handleChange({
        target: {
          name: 'mobileHome',
          value: []
        }
      });
  }

  const handleRestrictionsYesChange = (values: LineListRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.restrictionsAgreements?.length ?? 0) === 0)
      handleAddRestriction(values, handleChange);
    else if (!e.target.checked)
      handleChange({
        target: {
          name: 'restrictionsAgreements',
          value: []
        }
      });
  }

  const handleRestrictionsNoChange = (values: LineListRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.restrictionsAgreements?.length ?? 0) === 0)
      handleAddRestriction(values, handleChange);
    else if (e.target.checked)
      handleChange({
        target: {
          name: 'restrictionsAgreements',
          value: []
        }
      });
  }

  const handleEasementsYesChange = (values: LineListRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.easementsRightOfWays?.length ?? 0) === 0)
      handleAddEasement(values, handleChange);
    else if (!e.target.checked)
      handleChange({
        target: {
          name: 'easementsRightOfWays',
          value: []
        }
      });
  }

  const handleEasementsNoChange = (values: LineListRequest, handleChange: any) => (e: any): void => {
    if (!e.target.checked && (values.easementsRightOfWays?.length ?? 0) === 0)
      handleAddEasement(values, handleChange);
    else if (e.target.checked)
      handleChange({
        target: {
          name: 'easementsRightOfWays',
          value: []
        }
      });
  }

  const handleAddOwner = (values: LineListRequest, handleChange: any): void => {
    const newOwner = {
      name: '',
      email: '',
      phoneNumber: '',
      address: '',
      logInformation: {
        projectBudget: '',
        paymentOffer: '',
        remainingBudget: '',
        status: LogInfoStatus.INITIAL_CONTACT
      }
    };
    const newOwners = [...(values.ownerInformation ?? []), newOwner];
    handleChange({
      target: {
        name: 'ownerInformation',
        value: newOwners
      }
    });
  }

  const handleDeleteOwner = (values: LineListRequest, index: number, handleChange: any): void => {
    const newOwners = values.ownerInformation?.filter((_: any, i: number) => i !== index) ?? [];
    handleChange({
      target: {
        name: 'ownerInformation',
        value: newOwners
      }
    });
  }

  const handleTrueAddressYesChange = (values: LineListRequest, ownerIndex: number, handleChange: any) => (e: any): void => {
    if (e.target.checked) {
      const updatedOwners = [...(values.ownerInformation ?? [])];
      updatedOwners[ownerIndex] = {
        ...updatedOwners[ownerIndex],
        address: ''
      };
      handleChange({
        target: {
          name: 'ownerInformation',
          value: updatedOwners
        }
      });
    }
  }

  const handleTrueAddressNoChange = (values: LineListRequest, ownerIndex: number, handleChange: any) => (e: any): void => {
    if (e.target.checked) {
      const updatedOwners = [...(values.ownerInformation ?? [])];
      updatedOwners[ownerIndex] = {
        ...updatedOwners[ownerIndex],
        address: updatedOwners[ownerIndex].address || ' '
      };
      handleChange({
        target: {
          name: 'ownerInformation',
          value: updatedOwners
        }
      });
    }
  }

  const handleAddOccupant = (values: LineListRequest, handleChange: any): void => {
    const newOccupant = {
      name: '',
      email: '',
      phoneNumber: ''
    };
    const newOccupants = [...(values.occupantInformation ?? []), newOccupant];
    handleChange({
      target: {
        name: 'occupantInformation',
        value: newOccupants
      }
    });
  }

  const handleDeleteOccupant = (values: LineListRequest, index: number, handleChange: any): void => {
    const newOccupants = values.occupantInformation?.filter((_: any, i: number) => i !== index) ?? [];
    handleChange({
      target: {
        name: 'occupantInformation',
        value: newOccupants
      }
    });
  }

  const handleOccupantSameChange = (values: LineListRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked) {
      handleChange({
        target: {
          name: 'occupantInformation',
          value: []
        }
      });
    }
  }

  const handleOccupantDifferentChange = (values: LineListRequest, handleChange: any) => (e: any): void => {
    if (e.target.checked && (values.occupantInformation?.length ?? 0) === 0) {
      handleAddOccupant(values, handleChange);
    }
  }

  const handleLogInformationChange = (values: LineListRequest, ownerIndex: number, handleChange: any) => (e: any): void => {
    const { name, value } = e.target
    const fieldName = name.split('.')

    handleChange(e)

    if (fieldName[fieldName.length - 1] === 'projectBudget' || fieldName[fieldName.length - 1] === 'paymentOffer') {
      const owner = values.ownerInformation?.[ownerIndex]
      if (owner) {
        let projectBudget = owner.logInformation?.projectBudget ?? ''
        let paymentOffer = owner.logInformation?.paymentOffer ?? ''

        if (fieldName[fieldName.length - 1] === 'projectBudget') {
          projectBudget = value ?? ''
        } else if (fieldName[fieldName.length - 1] === 'paymentOffer') {
          paymentOffer = value ?? ''
        }

        const projectBudgetNum = parseFloat(projectBudget) || 0
        const paymentOfferNum = parseFloat(paymentOffer) || 0
        const remainingBudget = projectBudgetNum - paymentOfferNum

        handleChange({
          target: {
            name: `ownerInformation[${ownerIndex}].logInformation.remainingBudget`,
            value: String(remainingBudget)
          }
        })
      }
    }
  }

  const renderOrderInformation = (values: any, errors: any, touched: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
                Order Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            id='input-order-number'
            fullWidth
            label='Order Number'
            name='orderInformation.orderNumber'
            value={values.orderInformation?.orderNumber ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.orderInformation?.orderNumber && Boolean(errors.orderInformation?.orderNumber)}
            helperText={touched.orderInformation?.orderNumber && errors.orderInformation?.orderNumber}/>
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth error={touched.orderInformation?.searchType && Boolean(errors.orderInformation?.searchType)}>
            <InputLabel>Search Type</InputLabel>
            <Select
              id='input-search-type'
              name='orderInformation.searchType'
              value={values.orderInformation?.searchType ?? ''}
              label='Search Type'
              onBlur={handleBlur}
              onChange={handleChange}>
              {
                searchTypeOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
            <FormHelperText>{touched.orderInformation?.searchType && errors.orderInformation?.searchType}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <TextField
            id='input-search-start-date'
            fullWidth
            type='date'
            label='Search Start Date'
            name='orderInformation.searchStartDate'
            value={values.orderInformation?.searchStartDate ? moment(values.orderInformation.searchStartDate).local().format('YYYY-MM-DD') : ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.orderInformation?.searchStartDate && Boolean(errors.orderInformation?.searchStartDate)}
            helperText={touched.orderInformation?.searchStartDate && errors.orderInformation?.searchStartDate}
            InputLabelProps={{shrink: true}}/>
        </Grid>
        <Grid size={6}>
          <TextField
            id='input-search-end-date'
            fullWidth
            type='date'
            label='Search End Date'
            name='orderInformation.searchEndDate'
            value={values.orderInformation?.searchEndDate ? moment(values.orderInformation.searchEndDate).local().format('YYYY-MM-DD') : ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.orderInformation?.searchEndDate && Boolean(errors.orderInformation?.searchEndDate)}
            helperText={touched.orderInformation?.searchEndDate && errors.orderInformation?.searchEndDate}
            InputLabelProps={{shrink: true}}/>
        </Grid>
      </Grid>
    </Box>
  )

  const renderPropertyInformation = (values: any, errors: any, touched: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Property Information
      </Typography>
      <Grid container spacing={2}>
        <Grid size={6}>
          <FormControl fullWidth error={touched.propertyInformation?.stateID && Boolean(errors.propertyInformation?.stateID)}>
            <InputLabel>State</InputLabel>
            <Select
              id='input-state'
              name='propertyInformation.stateID'
              value={values.propertyInformation?.stateID ?? ''}
              label='State'
              onBlur={handleBlur}
              onChange={handleStateChange(handleChange)}>
              {
                stateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
            <FormHelperText>{touched.propertyInformation?.stateID && errors.propertyInformation?.stateID}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth error={touched.propertyInformation?.countyID && Boolean(errors.propertyInformation?.countyID)}>
            <InputLabel>County</InputLabel>
            <Select
              id='input-county'
              name='propertyInformation.countyID'
              value={values.propertyInformation?.countyID ?? ''}
              label='County'
              onBlur={handleBlur}
              onChange={handleCountyChange(handleChange)}>
              {
                countyOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
              }
            </Select>
            <FormHelperText>{touched.propertyInformation?.countyID && errors.propertyInformation?.countyID}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <TextField
            id='input-address'
            fullWidth
            label='Address'
            name='propertyInformation.propertyAddress'
            value={values.propertyInformation?.propertyAddress ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.propertyInformation?.propertyAddress && Boolean(errors.propertyInformation?.propertyAddress)}
            helperText={touched.propertyInformation?.propertyAddress && errors.propertyInformation?.propertyAddress}/>
        </Grid>
        <Grid size={6}>
          <TextField
            id='input-parcel-number'
            fullWidth
            label='Parcel Number'
            name='propertyInformation.parcelNumber'
            value={values.propertyInformation?.parcelNumber ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.propertyInformation?.parcelNumber && Boolean(errors.propertyInformation?.parcelNumber)}
            helperText={touched.propertyInformation?.parcelNumber && errors.propertyInformation?.parcelNumber}/>
        </Grid>
      </Grid>
    </Box>
  )

  const renderOwnerInformation = (values: any, errors: any, touched: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Owner Information
      </Typography>
      {
        values.ownerInformation?.map((owner: any, index: number) =>
          <Paper key={index} className='owner-information-item' elevation={1}>
            {
              (values.ownerInformation?.length ?? 0) > 1 &&
                <IconButton
                  aria-label='delete'
                  onClick={() => handleDeleteOwner(values, index, handleChange)}
                  className='owner-delete-button'>
                  <DeleteIcon color='error'/>
                </IconButton>
            }
            <Typography variant='h6' gutterBottom className='order-dialog-section'>
              Owner {index + 1}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label='Name'
                  name={`ownerInformation[${index}].name`}
                  value={owner.name ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}/>
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label='Email'
                  name={`ownerInformation[${index}].email`}
                  value={owner.email ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}/>
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label='Phone Number'
                  name={`ownerInformation[${index}].phoneNumber`}
                  value={owner.phoneNumber ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}/>
              </Grid>
              <Grid size={12}>
                <Box className='estates-checkbox-container'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='hasTrueAddress'
                        checked={!Boolean(owner.address)}
                        onChange={handleTrueAddressYesChange(values, index, handleChange)}/>
                    }
                    label='Primary Residence'/>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name='hasTrueAddress'
                        checked={Boolean(owner.address)}
                        onChange={handleTrueAddressNoChange(values, index, handleChange)}/>
                    }
                    label='NOT Primary Residence'/>
                </Box>
              </Grid>
              {
                Boolean(owner.address) &&
                  <Grid size={12}>
                    <Box className='true-address-container'>
                      <Typography variant='subtitle1' gutterBottom>
                        Owner's True Address
                      </Typography>
                      <TextField
                        fullWidth
                        label='True Address'
                        name={`ownerInformation[${index}].address`}
                        value={owner.address ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter the owner's actual residential address"
                        multiline
                        rows={3}
                        className='zitles-textarea'/>
                    </Box>
                  </Grid>
              }
              <Grid size={12}>
                <Typography variant='subtitle1' gutterBottom className='log-information-title'>
                  Log Information
                </Typography>
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label='Project Budget'
                  name={`ownerInformation[${index}].logInformation.projectBudget`}
                  value={owner.logInformation?.projectBudget ?? ''}
                  type={'number'}
                  onChange={handleLogInformationChange(values, index, handleChange)}
                  onBlur={handleBlur}
                  placeholder='Enter amount (e.g., 50000)'
                  InputProps={{
                    startAdornment: <InputAdornment position='start' className='dollar-sign-adornment'>$</InputAdornment>
                  }}/>
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label='Payment Offer'
                  name={`ownerInformation[${index}].logInformation.paymentOffer`}
                  value={owner.logInformation?.paymentOffer ?? ''}
                  onChange={handleLogInformationChange(values, index, handleChange)}
                  onBlur={handleBlur}
                  type={'number'}
                  placeholder='Enter amount (e.g., 10000)'
                  InputProps={{
                    startAdornment: <InputAdornment position='start' className='dollar-sign-adornment'>$</InputAdornment>
                  }}/>
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label='Remaining Budget'
                  name={`ownerInformation[${index}].logInformation.remainingBudget`}
                  value={owner.logInformation?.remainingBudget ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type={'number'}
                  placeholder='Auto-calculated or enter manually'
                  InputProps={{
                    startAdornment: <InputAdornment position='start' className='dollar-sign-adornment'>$</InputAdornment>
                  }}/>
              </Grid>
              <Grid size={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name={`ownerInformation[${index}].logInformation.status`}
                    value={owner.logInformation?.status ?? ''}
                    label='Status'
                    onBlur={handleBlur}
                    onChange={handleChange}>
                    <MenuItem value={LogInfoStatus.INITIAL_CONTACT}>Initial Contact</MenuItem>
                    <MenuItem value={LogInfoStatus.INITIAL_OFFER_SENT}>Initial Offer Sent</MenuItem>
                    <MenuItem value={LogInfoStatus.FINAL_OFFER_SENT}>Final Offer Sent</MenuItem>
                    <MenuItem value={LogInfoStatus.CONDEMNATION}>Condemnation</MenuItem>
                    <MenuItem value={LogInfoStatus.COMPLETED}>Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        )
      }
      <Button
        variant='outlined'
        startIcon={<AddIcon/>}
        onClick={() => handleAddOwner(values, handleChange)}
        className='zitles-button'>
        Add Additional Owner
      </Button>
    </Box>
  )

  const renderOccupantInformation = (values: any, errors: any, touched: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Occupant Information
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              name='hasOccupants'
              checked={(values.occupantInformation?.length ?? 0) === 0}
              onChange={handleOccupantSameChange(values, handleChange)}/>
          }
          label='Owner and Occupant are the same'/>
        <FormControlLabel
          control={
            <Checkbox
              name='hasOccupants'
              checked={(values.occupantInformation?.length ?? 0) > 0}
              onChange={handleOccupantDifferentChange(values, handleChange)}/>
          }
          label='Owner and Occupant are different'/>
      </Box>
      {
        (values.occupantInformation?.length ?? 0) > 0 &&
          <>
            {
              values.occupantInformation?.map((occupant: any, index: number) =>
                <Paper key={index} className='occupant-information-item' elevation={1}>
                  {
                    (values.occupantInformation?.length ?? 0) > 1 &&
                      <IconButton
                        aria-label='delete'
                        onClick={() => handleDeleteOccupant(values, index, handleChange)}
                        className='occupant-delete-button'>
                        <DeleteIcon color='error'/>
                      </IconButton>
                  }
                  <Typography variant='h6' gutterBottom className='order-dialog-section'>
                    Occupant {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label='Name'
                        name={`occupantInformation[${index}].name`}
                        value={occupant.name ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label='Email'
                        name={`occupantInformation[${index}].email`}
                        value={occupant.email ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        fullWidth
                        label='Phone Number'
                        name={`occupantInformation[${index}].phoneNumber`}
                        value={occupant.phoneNumber ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                  </Grid>
                </Paper>
              )
            }
            <Button
              variant='outlined'
              startIcon={<AddIcon/>}
              onClick={() => handleAddOccupant(values, handleChange)}
              className='zitles-button'>
              Add Additional Occupant
            </Button>
          </>
      }
    </Box>
  )

  const renderAcquisitionDetails = (values: any, errors: any, touched: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Acquisition Details
      </Typography>
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            fullWidth
            label='Size of Tract of Land'
            name='acquisitionDetails.tractSize'
            value={values.acquisitionDetails?.tractSize ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='e.g., 2.5 acres, 10,890 sq ft'/>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label='Size of Easement'
            name='acquisitionDetails.easementSize'
            value={values.acquisitionDetails?.easementSize ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='e.g., 30 ft wide, 0.25 acres'/>
        </Grid>
      </Grid>
    </Box>
  )

  const renderVestingDeed = (values: LineListRequest, errors: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Vesting Deed
      </Typography>
      {
        values.vestingDeed?.map((deed: VestingDeed, index: number) =>
          <Paper key={index} className='vesting-deed-item' elevation={1}>
            {
              (values.vestingDeed?.length ?? 0) > 1 &&
              <IconButton
                aria-label='delete'
                onClick={() => handleDeleteVestingDeed(values, index, handleChange)}
                className='vesting-deed-delete-button'>
                <DeleteIcon color='error'/>
              </IconButton>
            }
            <Grid container spacing={2}>
              <Grid size={3}>
                <TextField
                  fullWidth
                  type='date'
                  label='Recorded'
                  name={`vestingDeed[${index}].recordedDate`}
                  value={deed.recordedDate ? moment(deed.recordedDate).local().format('YYYY-MM-DD') : ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeed[${index}].recordedDate`])}
                  helperText={(errors as any)[`vestingDeed[${index}].recordedDate`]}
                  InputLabelProps={{shrink: true}}/>
              </Grid>
              <Grid size={3}>
                <TextField
                  fullWidth
                  label='Book'
                  name={`vestingDeed[${index}].book`}
                  value={deed.book ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeed[${index}].book`])}
                  helperText={(errors as any)[`vestingDeed[${index}].book`]}/>
              </Grid>
              <Grid size={3}>
                <TextField
                  fullWidth
                  label='Page'
                  name={`vestingDeed[${index}].page`}
                  value={deed.page ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeed[${index}].page`])}
                  helperText={(errors as any)[`vestingDeed[${index}].page`]}/>
              </Grid>
              <Grid size={3}>
                <TextField
                  fullWidth
                  label='Plat Reference'
                  name={`vestingDeed[${index}].platReference`}
                  value={deed.platReference ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeed[${index}].platReference`])}
                  helperText={(errors as any)[`vestingDeed[${index}].platReference`]}/>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label='Legal Description'
                  name={`vestingDeed[${index}].legalDescription`}
                  value={deed.legalDescription ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean((errors as any)[`vestingDeed[${index}].legalDescription`])}
                  helperText={(errors as any)[`vestingDeed[${index}].legalDescription`]}
                  multiline
                  rows={3}
                  className='zitles-textarea'/>
              </Grid>
            </Grid>
          </Paper>
        )}
      <Button
        variant='outlined'
        startIcon={<AddIcon/>}
        onClick={() => handleAddVestingDeed(values, handleChange)}
        className='zitles-button'>
        Add Additional Vesting Deed
      </Button>
    </Box>
  )

  const renderEstates = (values: LineListRequest, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Estates (Current Owner)
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              name='hasEstates'
              checked={(values.estates?.length ?? 0) > 0}
              onChange={handleEstatesYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              name='hasEstates'
              checked={(values.estates?.length ?? 0) === 0}
              onChange={handleEstatesNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        (values.estates?.length ?? 0) > 0 &&
          <>
            {
              values.estates?.map((estate: Estate, index: number) =>
                  <Paper key={index} className='vesting-deed-item' elevation={1}>
                    {
                      (values.estates?.length ?? 0) > 1 &&
                        <IconButton
                          aria-label='delete'
                          onClick={() => handleDeleteEstate(values, index, handleChange)}
                          className='vesting-deed-delete-button'>
                          <DeleteIcon color='error'/>
                        </IconButton>
                    }
                    <Typography variant='h6' gutterBottom className='order-dialog-section'>
                      Estate
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          label='Decedent Name'
                          name={`estates[${index}].decedentName`}
                          value={estate.decedentName ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}/>
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          label='Estate Number'
                          name={`estates[${index}].estateNumber`}
                          value={estate.estateNumber ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}/>
                      </Grid>
                      <Grid size={6}>
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select
                            name={`estates[${index}].estateStatus`}
                            value={estate.estateStatus ?? ''}
                            label='Status'
                            onBlur={handleBlur}
                            onChange={handleChange}>
                            <MenuItem value={EstateStatus.OPEN}>Open</MenuItem>
                            <MenuItem value={EstateStatus.CLOSED}>Closed</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={12}>
                        <Box className="estates-checkbox-container">
                          <FormControlLabel
                            control={<Checkbox
                              name={`estates[${index}].heirsListed`}
                              checked={estate.heirsListed ?? false}
                              onChange={handleChange}/>}
                            label="Heirs Listed"/>
                          <FormControlLabel
                            control={<Checkbox
                              name={`estates[${index}].heirsSearched`}
                              checked={estate.heirsSearched ?? false}
                              onChange={handleChange}/>}
                            label="Heirs Searched"/>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
              )}
            <Button
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddEstate(values, handleChange)}
              className="zitles-button">
              Add Estate
            </Button>
          </>
      }
    </Box>
  )

  const renderTaxes = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Taxes
      </Typography>
      {
        values.taxYears.map((taxYear: TaxYear, index: number) =>
          <Paper key={index} className='vesting-deed-item' elevation={1}>
            {
              values.taxYears.length > 1 &&
                <IconButton
                  aria-label="delete"
                  onClick={() => handleDeleteTaxYear(values, index, handleChange)}
                  className="vesting-deed-delete-button">
                  <DeleteIcon color="error"/>
                </IconButton>
            }
            <Typography variant='h6' gutterBottom className='order-dialog-section'>Tax</Typography>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label='Year'
                  name={`taxYears[${index}].year`}
                  value={taxYear.year ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}/>
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label='Amount Owed'
                  name={`taxYears[${index}].amountOwed`}
                  value={taxYear.amountOwed ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputProps={{
                    startAdornment: <InputAdornment position='start' className='dollar-sign-adornment'>$</InputAdornment>
                  }}/>
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label='Amount Paid'
                  name={`taxYears[${index}].amountPaid`}
                  value={taxYear.amountPaid ?? ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputProps={{
                    startAdornment: <InputAdornment position='start' className='dollar-sign-adornment'>$</InputAdornment>
                  }}/>
              </Grid>
            </Grid>
          </Paper>
        )
      }
      <Button
        variant='outlined'
        startIcon={<AddIcon/>}
        onClick={() => handleAddTaxYear(values, handleChange)}
        className='zitles-button'>
        Add Additional Year
      </Button>
      <Box className='tax-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              name='taxFlags.pendingTaxSale'
              checked={values.taxFlags?.pendingTaxSale ?? false}
              onChange={handleChange}/>
          }
          label='Pending Tax Sale?'/>
        <FormControlLabel
          control={
            <Checkbox
              name='taxFlags.delinquentTaxes'
              checked={values.taxFlags?.delinquentTaxes ?? false}
              onChange={handleChange}/>
          }
          label='Delinquent Taxes?'/>
        <FormControlLabel
          control={
            <Checkbox
              name='taxFlags.rollBackTaxes'
              checked={values.taxFlags?.rollBackTaxes ?? false}
              onChange={handleChange}/>
          }
          label='Roll Back Taxes?'/>
        <FormControlLabel
          control={
            <Checkbox
              name='taxFlags.taxExempt'
              checked={values.taxFlags?.taxExempt ?? false}
              onChange={handleChange}/>
          }
          label='Tax Exempt?'/>
      </Box>
    </Box>
  )

  const renderMobileHome = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Mobile Home
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              name='hasMobileHome'
              checked={values.mobileHome.length > 0}
              onChange={handleMobileHomeYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              name='hasMobileHome'
              checked={values.mobileHome.length === 0}
              onChange={handleMobileHomeNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.mobileHome.length > 0 &&
          <>
          {
            values.mobileHome.map((mobileHome: MobileHome, mobileIndex: number) =>
              <Paper key={mobileIndex} className="vesting-deed-item" elevation={1}>
                {
                  values.mobileHome.length > 1 &&
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDeleteMobileHome(values, mobileIndex, handleChange)}
                      className="vesting-deed-delete-button">
                      <DeleteIcon color="error"/>
                    </IconButton>
                }
                <Typography variant="h6" gutterBottom className="order-dialog-section">
                  Mobile Home
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={4}>
                    <TextField
                      fullWidth
                      label="Year"
                      name={`mobileHome[${mobileIndex}].year`}
                      value={mobileHome.year ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      fullWidth
                      label="Make"
                      name={`mobileHome[${mobileIndex}].make`}
                      value={mobileHome.make ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      fullWidth
                      label="Model"
                      name={`mobileHome[${mobileIndex}].model`}
                      value={mobileHome.model ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      fullWidth
                      label="Serial Number"
                      name={`mobileHome[${mobileIndex}].serialNumber`}
                      value={mobileHome.serialNumber ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      fullWidth
                      label="Parcel Number"
                      name={`mobileHome[${mobileIndex}].parcelNumber`}
                      value={mobileHome.parcelNumber ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      fullWidth
                      label="Decal"
                      name={`mobileHome[${mobileIndex}].decal`}
                      value={mobileHome.decal ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={12}>
                    <Box className="estates-checkbox-container">
                      <FormControlLabel
                        control={<Checkbox
                          name={`mobileHome[${mobileIndex}].lienRetirement`}
                          checked={mobileHome.lienRetirement !== undefined}
                          onChange={handleMobileHomeLienRetirementChange(values, mobileIndex, handleChange)}/>}
                        label="Lien Retirement?"/>
                      <FormControlLabel
                        control={<Checkbox
                          name={`mobileHome[${mobileIndex}].taxYears`}
                          checked={mobileHome.taxYears && mobileHome.taxYears.length > 0}
                          onChange={handleChange}/>}
                        label="Mobile Home Tax Years?"/>
                    </Box>
                  </Grid>
                  {mobileHome.lienRetirement !== undefined && (<Grid size={12}>
                      <Typography variant="h6" gutterBottom className="order-dialog-section">
                        Lien Retirement
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={4}>
                          <TextField
                            fullWidth
                            type="date"
                            label="Recorded Date"
                            name={`mobileHome[${mobileIndex}].lienRetirement.recordedDate`}
                            value={mobileHome.lienRetirement.recordedDate ? moment(mobileHome.lienRetirement.recordedDate).local().format('YYYY-MM-DD') : ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            InputLabelProps={{shrink: true}}/>
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            fullWidth
                            label="Book"
                            name={`mobileHome[${mobileIndex}].lienRetirement.book`}
                            value={mobileHome.lienRetirement.book ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}/>
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            fullWidth
                            label="Page"
                            name={`mobileHome[${mobileIndex}].lienRetirement.page`}
                            value={mobileHome.lienRetirement.page ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}/>
                        </Grid>
                      </Grid>
                    </Grid>)}
                  {mobileHome.taxYears && mobileHome.taxYears.length > 0 && <Grid size={12}>
                    <Typography variant="h6" gutterBottom className="order-dialog-section">
                      Mobile Home Tax Years
                    </Typography>
                    {mobileHome.taxYears.map((taxYear: MobileHomeTaxYear, taxIndex: number) => <Paper key={taxIndex} className="vesting-deed-item" elevation={1}>
                      {mobileHome.taxYears && mobileHome.taxYears.length > 1 && <IconButton
                        aria-label="delete"
                        onClick={() => handleDeleteMobileHomeTaxYear(values, mobileIndex, taxIndex, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>}
                      <Grid container spacing={2}>
                        <Grid size={4}>
                          <TextField
                            fullWidth
                            label="Year"
                            name={`mobileHome[${mobileIndex}].taxYears[${taxIndex}].year`}
                            value={taxYear.year ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}/>
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            fullWidth
                            label="Amount Owed"
                            name={`mobileHome[${mobileIndex}].taxYears[${taxIndex}].amountOwed`}
                            value={taxYear.amountOwed ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            InputProps={{
                              startAdornment: <InputAdornment position="start" className="dollar-sign-adornment">$</InputAdornment>
                            }}/>
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            fullWidth
                            label="Amount Paid"
                            name={`mobileHome[${mobileIndex}].taxYears[${taxIndex}].amountPaid`}
                            value={taxYear.amountPaid ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            InputProps={{
                              startAdornment: <InputAdornment position="start" className="dollar-sign-adornment">$</InputAdornment>
                            }}/>
                        </Grid>
                      </Grid>
                    </Paper>)}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon/>}
                      onClick={() => handleAddMobileHomeTaxYear(values, mobileIndex, handleChange)}
                      className="zitles-button">
                      Add Mobile Home Tax Year
                    </Button>
                  </Grid>}
                </Grid>
              </Paper>
            )
          }
          <Button
            variant="outlined"
            startIcon={<AddIcon/>}
            onClick={() => handleAddMobileHome(values, handleChange)}
            className="zitles-button">
            Add Mobile Home
          </Button>
        </>
      }
    </Box>
  )

  const renderRestrictions = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Restrictions / Agreements
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              name='hasRestrictions'
              checked={values.restrictionsAgreements.length > 0}
              onChange={handleRestrictionsYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              name='hasRestrictions'
              checked={values.restrictionsAgreements.length === 0}
              onChange={handleRestrictionsNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.restrictionsAgreements.length > 0 &&
          <>
            {
              values.restrictionsAgreements.map((restriction: RestrictionAgreement, index: number) =>
                <Paper key={index} className="vesting-deed-item" elevation={1}>
                  {
                    values.restrictionsAgreements.length > 1 &&
                      <IconButton
                        aria-label="delete"
                        onClick={() => handleDeleteRestriction(values, index, handleChange)}
                        className="vesting-deed-delete-button">
                        <DeleteIcon color="error"/>
                      </IconButton>
                  }
                  <Typography variant="h6" gutterBottom className="order-dialog-section">
                    Restriction/Agreement
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={4}>
                      <TextField
                        fullWidth
                        label="Book"
                        name={`restrictionsAgreements[${index}].book`}
                        value={restriction.book ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        fullWidth
                        label="Page"
                        name={`restrictionsAgreements[${index}].page`}
                        value={restriction.page ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={4}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Recorded Date"
                        name={`restrictionsAgreements[${index}].recordedDate`}
                        value={restriction.recordedDate ? moment(restriction.recordedDate).local().format('YYYY-MM-DD') : ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{shrink: true}}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Title"
                        name={`restrictionsAgreements[${index}].title`}
                        value={restriction.title ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}/>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        name={`restrictionsAgreements[${index}].description`}
                        value={restriction.description ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        rows={3}
                        className="zitles-textarea"/>
                    </Grid>
                  </Grid>
                </Paper>
              )
            }
            <Button
              variant="outlined"
              startIcon={<AddIcon/>}
              onClick={() => handleAddRestriction(values, handleChange)}
              className="zitles-button">
              Add Restriction/Agreement
            </Button>
          </>
      }
    </Box>
  )

  const renderEasements = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        Easements & Right of Ways
      </Typography>
      <Box className='estates-checkbox-container'>
        <FormControlLabel
          control={
            <Checkbox
              name='hasEasements'
              checked={values.easementsRightOfWays.length > 0}
              onChange={handleEasementsYesChange(values, handleChange)}/>
          }
          label='Yes'/>
        <FormControlLabel
          control={
            <Checkbox
              name='hasEasements'
              checked={values.easementsRightOfWays.length === 0}
              onChange={handleEasementsNoChange(values, handleChange)}/>
          }
          label='No'/>
        <Typography variant='body2' color='text.secondary' className='estates-checkbox-text'>
          (If yes, complete below section)
        </Typography>
      </Box>
      {
        values.easementsRightOfWays.length > 0 &&
        <>
          {
            values.easementsRightOfWays.map((easement: EasementRightOfWay, index: number) =>
              <Paper key={index} className="vesting-deed-item" elevation={1}>
                {
                  values.easementsRightOfWays.length > 1 &&
                    <IconButton
                    aria-label="delete"
                    onClick={() => handleDeleteEasement(values, index, handleChange)}
                    className="vesting-deed-delete-button">
                    <DeleteIcon color="error"/>
                  </IconButton>
                }
                <Typography variant="h6" gutterBottom className="order-dialog-section">
                  Easement
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={3}>
                    <TextField
                      fullWidth
                      label="Book"
                      name={`easementsRightOfWays[${index}].book`}
                      value={easement.book ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={3}>
                    <TextField
                      fullWidth
                      label="Page"
                      name={`easementsRightOfWays[${index}].page`}
                      value={easement.page ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Recorded Date"
                      name={`easementsRightOfWays[${index}].recordedDate`}
                      value={easement.recordedDate ? moment(easement.recordedDate).local().format('YYYY-MM-DD') : ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      InputLabelProps={{shrink: true}}/>
                  </Grid>
                  <Grid size={3}>
                    <TextField
                      fullWidth
                      label="Type"
                      name={`easementsRightOfWays[${index}].type`}
                      value={easement.type ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Title"
                      name={`easementsRightOfWays[${index}].title`}
                      value={easement.title ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}/>
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name={`easementsRightOfWays[${index}].description`}
                      value={easement.description ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      multiline
                      rows={3}
                      className="zitles-textarea"/>
                  </Grid>
                </Grid>
              </Paper>
            )
          }
          <Button
            variant="outlined"
            startIcon={<AddIcon/>}
            onClick={() => handleAddEasement(values, handleChange)}
            className="zitles-button">
            Add Easement
          </Button>
        </>
      }
    </Box>
  )

  const renderGeneralComments = (values: any, handleChange: any, handleBlur: any): JSX.Element => (
    <Box className='title-summary-report-section'>
      <Typography variant='h6' gutterBottom className='order-dialog-section'>
        General Comments
      </Typography>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            label='General Comments'
            name='comment'
            value={values.comment ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            rows={4}
            className='zitles-textarea'/>
        </Grid>
      </Grid>
    </Box>
  )

  const renderActionButtons = (isSubmitting: boolean): JSX.Element => {
    const props = lineListRequestService.getCurrentProps();
    const isUpdate = props?.mode === ManageMode.UPDATE && Boolean(props?.reportData);

    return (
      <Box className='action-btn-container'>
        <Button
          type='submit'
          disabled={isSubmitting}
          className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
          startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
          {isSubmitting ? (isUpdate ? 'Updating Report' : 'Uploading Report') : (isUpdate ? 'Update Report' : 'Save Report')}
        </Button>
      </Box>
    );
  }

  return (
    <Box className='title-summary-report-container'>
      {
        !isDataLoaded
          ? <LoadingComponent />
          : <Paper elevation={2} className='title-summary-report-paper'>
              <Formik
                initialValues={lineListRequestService.getLineListRequestDto()}
                enableReinitialize={true}
                onSubmit={handleSubmit}>
                {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting}: FormikProps<LineListRequest>): JSX.Element => {
                  return (
                    <>
                      <Box className='title-summary-report-header'>
                        <Typography variant='h5' className='zitles-dialog-title report-title'>
                          Line List
                        </Typography>
                        <Box className='template-buttons-container'>
                          <Button
                            id='btn-import-template'
                            variant='outlined'
                            startIcon={<DownloadIcon/>}
                            onClick={handleImportTemplate}
                            className='zitles-button'>
                            Import Template
                          </Button>
                          <Button
                            id='btn-export-template'
                            variant='outlined'
                            startIcon={<UploadIcon/>}
                            onClick={() => handleExportTemplate(values)}
                            className='zitles-button'>
                            Export Template
                          </Button>
                        </Box>
                      </Box>
                      <Form onSubmit={handleSubmit}>
                        {renderOrderInformation(values, errors, touched, handleChange, handleBlur)}

                        {renderPropertyInformation(values, errors, touched, handleChange, handleBlur)}

                        {renderOwnerInformation(values, errors, touched, handleChange, handleBlur)}

                        {renderOccupantInformation(values, errors, touched, handleChange, handleBlur)}

                        {renderAcquisitionDetails(values, errors, touched, handleChange, handleBlur)}

                        {renderVestingDeed(values, errors, handleChange, handleBlur)}

                        {renderEstates(values, handleChange, handleBlur)}

                        {renderTaxes(values, handleChange, handleBlur)}

                        {renderMobileHome(values, handleChange, handleBlur)}

                        {renderRestrictions(values, handleChange, handleBlur)}

                        {renderEasements(values, handleChange, handleBlur)}

                        {renderGeneralComments(values, handleChange, handleBlur)}

                        {renderActionButtons(isSubmitting)}
                      </Form>
                    </>
                  );
                }}
              </Formik>
            </Paper>
      }
      {
        openTemplateDialog &&
          <LineListTemplateDialog
            open={openTemplateDialog}
            onClose={() => setOpenTemplateDialog(false)}
            lineListRequest={currentFormValues}
            mode={dialogMode}
            onTemplateSelect={dialogMode === ManageMode.IMPORT ? handleTemplateSelect : undefined}/>
      }
    </Box>
  );
}

export default observer(LineListReport);
